import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clientsService, type ClientsFilters, type ClientsResponse, type ClientsStatistics, type FilterOptions } from '../services/clientsService';

// Ключи для кеша
export const clientsKeys = {
  all: ['clients'] as const,
  list: (filters: ClientsFilters = {}) => [...clientsKeys.all, 'list', filters] as const,
  statistics: () => [...clientsKeys.all, 'statistics'] as const,
  filterOptions: () => [...clientsKeys.all, 'filterOptions'] as const,
};

// Placeholder данные для мгновенного отображения UI
const placeholderClientsResponse: ClientsResponse = {
  data: [],
  total: 0,
  per_page: 100,
  current_page: 1,
  last_page: 1,
};

const placeholderStatistics: ClientsStatistics = {
  total_clients: 0,
  clients_this_month: 0,
};

const placeholderFilterOptions: FilterOptions = {
  client_names: [],
  curators: [],
  fields_of_activity: [],
  years: [],
};

/**
 * Хук для получения списка клиентов
 */
export const useClients = (filters: ClientsFilters = {}, enabled: boolean = true) => {
  const queryClient = useQueryClient();
  
  const cacheKey = clientsKeys.list(filters);
  
  // Получаем данные из кеша синхронно
  const cachedData = queryClient.getQueryData<ClientsResponse>(cacheKey);
  
  const query = useQuery({
    queryKey: cacheKey,
    queryFn: () => clientsService.getClients(filters),
    enabled,
    staleTime: 1000 * 30, // 30 секунд - данные считаются свежими
    gcTime: 1000 * 60 * 30, // 30 минут - время хранения в кеше
    placeholderData: (previousData) => {
      if (previousData) return previousData;
      return cachedData ?? placeholderClientsResponse;
    },
    // Важно: initialData задаем только из реального кеша.
    // Если подставить placeholder как initialData, React Query может посчитать
    // такой ответ "свежим" и не выполнить запрос для нового фильтра.
    initialData: cachedData,
    initialDataUpdatedAt: cachedData ? Date.now() - 1000 : undefined,
    // Нужно, чтобы после редактирования клиента список обновлялся,
    // даже если у нас есть данные в кеше.
    refetchOnMount: true,
  });
  
  const data = query.data ?? cachedData ?? placeholderClientsResponse;
  
  return {
    ...query,
    data,
    isLoading: query.isLoading && !cachedData && !query.data,
  };
};

/**
 * Хук для получения статистики клиентов
 */
export const useClientsStatistics = (enabled: boolean = true) => {
  const queryClient = useQueryClient();
  
  const cacheKey = clientsKeys.statistics();
  const cachedData = queryClient.getQueryData<ClientsStatistics>(cacheKey);
  const initialData = cachedData ?? placeholderStatistics;
  
  const query = useQuery({
    queryKey: cacheKey,
    queryFn: () => clientsService.getStatistics(),
    enabled,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 30,
    placeholderData: (previousData) => {
      if (previousData) return previousData;
      return cachedData ?? placeholderStatistics;
    },
    initialData: initialData,
    initialDataUpdatedAt: cachedData ? Date.now() - 1000 : undefined,
    refetchOnMount: true,
  });
  
  const data = query.data ?? cachedData ?? placeholderStatistics;
  
  return {
    ...query,
    data,
    isLoading: query.isLoading && !cachedData && !query.data,
  };
};

/**
 * Хук для получения опций фильтров
 */
export const useClientsFilterOptions = (enabled: boolean = true) => {
  const queryClient = useQueryClient();
  
  const cacheKey = clientsKeys.filterOptions();
  const cachedData = queryClient.getQueryData<FilterOptions>(cacheKey);
  const initialData = cachedData ?? placeholderFilterOptions;
  
  const query = useQuery({
    queryKey: cacheKey,
    queryFn: () => clientsService.getFilterOptions(),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 минут - опции фильтров меняются редко
    gcTime: 1000 * 60 * 30,
    placeholderData: (previousData) => {
      if (previousData) return previousData;
      return cachedData ?? placeholderFilterOptions;
    },
    initialData: initialData,
    initialDataUpdatedAt: cachedData ? Date.now() - 1000 : undefined,
    refetchOnMount: false,
  });
  
  const data = query.data ?? cachedData ?? placeholderFilterOptions;
  
  return {
    ...query,
    data,
    isLoading: query.isLoading && !cachedData && !query.data,
  };
};
